import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Download, QrCode } from 'lucide-react';
import axios from 'axios';

const QRGenerator = () => {
  const [data, setData] = useState('');
  const [size, setSize] = useState([10]);
  const [errorCorrection, setErrorCorrection] = useState('M');
  const [fillColor, setFillColor] = useState('#000000');
  const [backColor, setBackColor] = useState('#ffffff');
  const [qrImage, setQrImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const generateQR = async () => {
    if (!data.trim()) {
      setError('Please enter some data');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/qr/generate`,
        {
          data,
          size: size[0],
          error_correction: errorCorrection,
          fill_color: fillColor,
          back_color: backColor
        },
        { responseType: 'blob' }
      );

      const imageUrl = URL.createObjectURL(response.data);
      setQrImage(imageUrl);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = () => {
    if (qrImage) {
      const link = document.createElement('a');
      link.href = qrImage;
      link.download = 'qrcode.png';
      link.click();
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">QR Code Generator</h1>
        <p className="text-muted-foreground">
          Create custom QR codes for URLs, text, WiFi, contact info, and more
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>QR Code Settings</CardTitle>
            <CardDescription>Customize your QR code</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="data">Data (URL, Text, etc.)</Label>
              <Input
                id="data"
                placeholder="https://example.com or any text"
                value={data}
                onChange={(e) => setData(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Size: {size[0]}</Label>
              <Slider
                value={size}
                onValueChange={setSize}
                min={5}
                max={20}
                step={1}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="error-correction">Error Correction</Label>
              <Select value={errorCorrection} onValueChange={setErrorCorrection}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="L">Low (7%)</SelectItem>
                  <SelectItem value="M">Medium (15%)</SelectItem>
                  <SelectItem value="Q">Quartile (25%)</SelectItem>
                  <SelectItem value="H">High (30%)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fill-color">Foreground Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="fill-color"
                    type="color"
                    value={fillColor}
                    onChange={(e) => setFillColor(e.target.value)}
                    className="w-16 h-10"
                  />
                  <Input
                    value={fillColor}
                    onChange={(e) => setFillColor(e.target.value)}
                    placeholder="#000000"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="back-color">Background Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="back-color"
                    type="color"
                    value={backColor}
                    onChange={(e) => setBackColor(e.target.value)}
                    className="w-16 h-10"
                  />
                  <Input
                    value={backColor}
                    onChange={(e) => setBackColor(e.target.value)}
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </div>

            <Button onClick={generateQR} disabled={loading} className="w-full">
              <QrCode className="mr-2 h-4 w-4" />
              {loading ? 'Generating...' : 'Generate QR Code'}
            </Button>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>Your generated QR code</CardDescription>
          </CardHeader>
          <CardContent>
            {qrImage ? (
              <div className="space-y-4">
                <div className="flex justify-center p-4 bg-muted rounded-lg">
                  <img src={qrImage} alt="QR Code" className="max-w-full" />
                </div>
                <Button onClick={downloadQR} className="w-full">
                  <Download className="mr-2 h-4 w-4" />
                  Download QR Code
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-muted rounded-lg">
                <p className="text-muted-foreground">QR code will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Common Use Cases</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <h4 className="font-semibold mb-1">Website URL</h4>
              <code className="text-xs bg-muted p-1 rounded">https://example.com</code>
            </div>
            <div>
              <h4 className="font-semibold mb-1">WiFi Network</h4>
              <code className="text-xs bg-muted p-1 rounded">WIFI:T:WPA;S:NetworkName;P:password;;</code>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Contact (vCard)</h4>
              <code className="text-xs bg-muted p-1 rounded">BEGIN:VCARD...</code>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QRGenerator;
